import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {WidgetDetail} from '../../../../../../../domain/widget/model/widget-detail';
import {WidgetSize} from '../../../../../../../domain/widget/model/widget-size.enum';
import {InviseoVariablesExtraction} from '../../../../../../../domain/inviseo/model/inviseo-variables-extraction';
import {Chart} from 'angular-highcharts';
import * as Highcharts from 'highcharts';
import {Options, PointOptionsObject, SeriesOptionsType} from 'highcharts';
import {TranslateService} from '@ngx-translate/core';
import {VariableValues} from '../../../../../../../domain/inviseo/model/variable-values';
import {InviseoWidgetUtilService} from '../../../../../../../domain/inviseo/service/inviseo-widget-util.service';
import {AmountFormatterService} from '../../../../../../services/amount-formatter.service';
import {Amount} from '../../../../../../../domain/shared/amount';

@Component({
  selector: 'app-widget-inviseo-variables',
  templateUrl: './home-dashboard-widget-inviseo-variables.component.html'
})
export class HomeDashboardWidgetInviseoVariablesComponent implements OnInit {

  @Input() widget: WidgetDetail | undefined;
  @Input() inviseoVariablesExtraction: InviseoVariablesExtraction;
  public WidgetSize = WidgetSize;
  public chart: Chart;

  @Output() actionEvent = new EventEmitter<string>();

  constructor(private translateService: TranslateService,
              private amountFormatterService: AmountFormatterService,
              private inviseoWidgetUtilService: InviseoWidgetUtilService) {
  }

  ngOnInit(): void {
    if (this.inviseoVariablesExtraction) {
      this.chart = this.loadChart();
    }
  }

  public getLegendStyle(index: number): string {
    return index === 0 ? 'color: #2EA7AA' : 'color: #FF8E00';
  }

  public getRedirectLink(): string {
    return this.inviseoWidgetUtilService.getRedirectLink(this.widget, this.inviseoVariablesExtraction);
  }

  private loadChart(): Chart {
    const spec: Options = {
      title: {
        text: null
      },
      subtitle: {
        text: null
      },
      xAxis: {
        categories: Object.keys(this.inviseoVariablesExtraction.variableValues[0].valuesByYear),
        title: {
          text: null
        },
        labels: {
          step: this.getStep(),
          y: 10,
          distance: 5,
          style: {
            fontSize: '8px',
            color: '#6a7480'
          },
          rotation: 0,
          padding: 0
        }
      },
      yAxis: this.getYAxis(),
      legend: {
        enabled: false
      },
      tooltip: {
        style: {
          color: '#333333',
          cursor: 'default',
          fontSize: '12px',
          pointerEvents: 'none',
          whiteSpace: 'nowrap'
        },
        formatter(): string {
          return this.points.reduce((s, point: any) => {
            return s + '<span style="font-size:12px;">' + point.point.name + '</span>: <b>'
              + '<span style="font-weight:bold;">' + point.point.formattedAmount + '</span></b>' +
              '<br/>';
          }, this.x + '<br/>');
        },
        shared: true,
        outside: true
      },
      plotOptions: {
        series: {
          label: {
            enabled: false,
          }
        }
      },
      credits: {
        enabled: false,
      },
      exporting: {
        enabled: false,
      },
      colors: [
        '#2EA7AA',
        '#FF8E00',
        '#E95053',
        '#008CBA'
      ],
      series: this.getSeries()
    };

    return new Chart(spec as Highcharts.Options);
  }

  private getStep(): number {
    return this.widget.size === WidgetSize.SMALL ? 2 : 1;
  }

  private getYAxis(): any {
    return this.inviseoVariablesExtraction.variableValues.map((variable, index) => this.getYAxisFromVariableValue(variable, index));
  }

  private getYAxisFromVariableValue(variableValues: VariableValues, index: number): any {
    const amountFormatter = this.amountFormatterService;
    const formatValue = this.formatValue;
    return {
      tickAmount: 4,
      opposite: index === 1,
      title: {
        text: null
      },
      labels: {
        formatter(): string {
          return formatValue(variableValues.valueType, this.value, amountFormatter, '0a', true);
        }
      }
    };
  }

  private getSeries(): SeriesOptionsType[] {
    return this.inviseoVariablesExtraction.variableValues.map((variable, index) => this.getSeriesFromVariableValues(variable, index));
  }

  private getSeriesFromVariableValues(variableValues: VariableValues, index: number): any {
    return {
      name: this.translateService.instant('widget.inviseo-variables.series.name.' + variableValues.variableCode),
      type: this.inviseoWidgetUtilService.getChartTypeForVariableCode(variableValues.variableCode),
      data: this.getSeriesData(variableValues),
      yAxis: this.getYAxisForSeries(variableValues, index)
    };
  }

  private getSeriesData(variableValues: VariableValues): PointOptionsObject {
    const result = [];

    Object.values(variableValues.valuesByYear).forEach((value) => {
      result.push({
        name: this.translateService.instant('widget.inviseo-variables.series.name.' + variableValues.variableCode),
        y: value,
        formattedAmount: this.formatValue(variableValues.valueType, value, this.amountFormatterService, '0,0', false)
      });
    });

    return result;
  }

  private formatValue(valueType: string, value: any, amountFormatterService: AmountFormatterService, formatter: string, isLegend: boolean): string {
    if (valueType === 'YEAR') {
      return value + ' A';
    } else if (valueType === 'POURCENTAGE') {
      return value + ' %';
    } else if (valueType === 'AMOUNT') {
      const valueToReturn = amountFormatterService.format(new Amount(value, '€', false), formatter);
      if (isLegend) {
        if (value < 0) {
          return '<span style="color:red">' + valueToReturn + '</span>';
        } else {
          return '<span>' + valueToReturn + '</span>';
        }
      } else {
        return valueToReturn;
      }
    } else {
      return value;
    }
  }

  private getYAxisForSeries(variableValues: VariableValues, index: number): number {
    return variableValues.variableCode === 'SEUIL_LIMITE_RATIO_DESENDETTEMENT' ? 0 : index;
  }

}
